import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

type UseBestPracticeComplianceStandardsQueryArgs = {
  clusterIds?: string[];
  enabled?: boolean;
};

const SECURITY_BEST_COMPLIANCE_STANDARDS_QUERY_KEY =
  'security-best-compliance-standards';

const getBestPracticeComplianceStandardsQueryKey = (clusterIds?: string[]) =>
  [SECURITY_BEST_COMPLIANCE_STANDARDS_QUERY_KEY, clusterIds] as const;

const getBestPracticeComplianceStandardsQueryOptions = ({
  clusterIds,
  enabled = true,
}: UseBestPracticeComplianceStandardsQueryArgs) =>
  queryOptions({
    queryKey: getBestPracticeComplianceStandardsQueryKey(clusterIds),
    queryFn: async () => {
      const { data } =
        await apiClient.securityInsights.getBestPracticeComplianceStandards({
          clusterIds: clusterIds || [],
        });
      return data.standards;
    },
    select: (data) => {
      return data
        ?.filter((standard) => !standard.id?.startsWith('cis')) // Filter CIS standards from showing in the UI
        ?.map((standard) => ({
          title: standard.name!,
          value: standard.id!,
        }));
    },
    enabled: enabled && !!clusterIds?.length,
  });

export const useBestPracticeComplianceStandardsQuery = (
  params: UseBestPracticeComplianceStandardsQueryArgs = {}
) => {
  const {
    data: complianceStandards,
    isLoading,
    error,
    refetch,
  } = useQuery(getBestPracticeComplianceStandardsQueryOptions(params));
  return { complianceStandards, isLoading, error, refetch };
};
